import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RouteComponentProps, Switch, Route, Redirect } from 'react-router-dom';

import { ENetworkRequestStatus } from 'services/BackendApi/types/Generic';
import * as AuthSelectors from 'store/modules/auth';
import * as BookingManagerActions from 'store/modules/bookingManager/actions';

import * as BookingManagerSelectors from 'store/modules/bookingManager/selectors';

import { SidebarNav, NavItem } from 'pureUi/SidebarNav';
import { settings } from 'ui/Icons';

import { NavigationLayoutConnected } from 'containers/NavigationLayout';
import BookingManagerBreakdown from 'containers/BookingManagerBreakdown';
import BookingManagerGuestInformation from 'containers/BookingManagerGuestInformation';
import BookingManagerCommentsConnected from 'containers/BookingManagerComments';
import BookingManagerDownloadsConnected from 'containers/BookingManagerDownloads';
import BookingManagerUploads from 'containers/BookingManagerUploads';
import { BookingManagerVouchersV2 } from 'containers/BookingManagerVouchersV2';
import BookingManagerFinanceConnected from 'containers/BookingManagerFinance';
import BookingManagerPurchaseOrders from 'containers/BookingManagerPurchaseOrders';
import BookingManagerDashboard from 'containers/BookingManagerDashboard';
import BookingManagerSettings from 'containers/BookingManagerSettings';
import { BookingManagerItinerary } from 'containers/BookingManagerItinerary/BookingManagerItinerary';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

import { EBookingManageRoutes } from './routes';
import { BookingManagerStyles } from './BookingManagerStyles';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { BookingManagerGuestAllocation } from 'containers/BookingManagerGuestAllocation/BookingManagerGuestAllocation';

interface IRouteParams {
  bookingUuid: string;
}
export interface IBookingManagerContainerProps extends RouteComponentProps<IRouteParams> {
  className?: string;
}
export const BookingManagerContainer = (props: IBookingManagerContainerProps) => {
  const dispatch = useDispatch();
  const booking = useSelector(BookingManagerSelectors.bookingSelector);
  const bookingLoadRequestStatus = useSelector(BookingManagerSelectors.bookingLoadSelector);
  const isSr = useSelector(AuthSelectors.isInternalUser);
  const isTA = useSelector(AuthSelectors.isTA);
  const hideFinance = useSelector(AuthSelectors.hideFinanceSelector);
  const isAdmin = useSelector(AuthSelectors.isAdmin);
  const isFinance = useSelector(AuthSelectors.isFinanceUser);
  const isSales = useSelector(AuthSelectors.isSales);
  const { dynamicParameters } = useDynamicParameters();
  const { bookingUuid } = props.match.params;

  useEffect(() => {
    dispatch(BookingManagerActions.getBookingRequestAction(bookingUuid));
    return () => {
      dispatch(BookingManagerActions.resetBookingAction());
    };
  }, [bookingUuid]);

  const getRoutePath = (route: EBookingManageRoutes) => `${props.match.url}/${route}`;

  const isInternalUser = () => {
    return isSr || isAdmin;
  };

  const showAllocationGuestTab = () => {
    return (isTA || isSales || isInternalUser()) && dynamicParameters.ENABLE_GUEST_ALLOCATION_TEMP;
  };

  const canSeePurchaseOrders = () => {
    return (isInternalUser() || isFinance) && dynamicParameters.ENABLE_PURCHASE_ORDERS;
  };

  const renderMenu = () => (
    <SidebarNav className="bookingManageNav">
      <NavItem to={getRoutePath(EBookingManageRoutes.Overview)}>Overview</NavItem>
      <NavItem to={getRoutePath(EBookingManageRoutes.Breakdown)}>Breakdown</NavItem>
      {dynamicParameters.ENABLE_BOOKING_ITINERARY_TEMP && (
        <NavItem to={getRoutePath(EBookingManageRoutes.Itinerary)}>Itinerary</NavItem>
      )}
      <NavItem to={getRoutePath(EBookingManageRoutes.GuestInformation)}>Guest Information</NavItem>
      {showAllocationGuestTab() && (
        <NavItem to={getRoutePath(EBookingManageRoutes.GuestAllocation)}>Guest Allocation</NavItem>
      )}
      <NavItem to={getRoutePath(EBookingManageRoutes.Comments)}>Comments</NavItem>
      <NavItem to={getRoutePath(EBookingManageRoutes.HotelDocuments)}>Hotel Documents</NavItem>
      <NavItem to={getRoutePath(EBookingManageRoutes.Uploads)}>Booking Documents</NavItem>
      {isInternalUser() && <NavItem to={getRoutePath(EBookingManageRoutes.Vouchers)}>Voucher</NavItem>}
      {!hideFinance && <NavItem to={getRoutePath(EBookingManageRoutes.Finance)}>Finance</NavItem>}
      {canSeePurchaseOrders() && (
        <NavItem to={getRoutePath(EBookingManageRoutes.PurchaseOrders)}>Purchase Orders</NavItem>
      )}
      {isInternalUser() && (
        <NavItem to={getRoutePath(EBookingManageRoutes.Settings)}>
          <div className="flex items-center" style={{ height: '34px' }}>
            <img className="mr-2" src={settings} width="16" height="16" alt="Settings" />
            <span className="leading-20px">Settings</span>
          </div>
        </NavItem>
      )}
    </SidebarNav>
  );

  if (bookingLoadRequestStatus === ENetworkRequestStatus.PENDING || booking?.bookingUuid !== bookingUuid) {
    return (
      <BookingManagerStyles>
        <div className="mt-5">
          <LoadingBar label="Loading booking..." />
        </div>
      </BookingManagerStyles>
    );
  }

  if (bookingLoadRequestStatus === ENetworkRequestStatus.ERROR) {
    return (
      <div>
        <p>There was an error loading the booking. Please try again.</p>
      </div>
    );
  }

  if (!booking) {
    return <p>No booking</p>;
  }

  return (
    <BookingManagerStyles>
      {/* @ts-ignore */}
      <NavigationLayoutConnected menuPath="/" menu={renderMenu()}>
        <div className="content">
          <section className="routeContent flex flex-col">
            <Switch>
              <Route exact path={getRoutePath(EBookingManageRoutes.Overview)} component={BookingManagerDashboard} />
              <Route exact path={getRoutePath(EBookingManageRoutes.Breakdown)} component={BookingManagerBreakdown} />
              <Route exact path={getRoutePath(EBookingManageRoutes.Itinerary)} component={BookingManagerItinerary} />
              <Route
                exact
                path={getRoutePath(EBookingManageRoutes.GuestInformation)}
                component={BookingManagerGuestInformation}
              />
              {showAllocationGuestTab() && (
                <Route
                  exact
                  path={getRoutePath(EBookingManageRoutes.GuestAllocation)}
                  component={BookingManagerGuestAllocation}
                />
              )}
              <Route
                exact
                path={getRoutePath(EBookingManageRoutes.Comments)}
                component={BookingManagerCommentsConnected}
              />
              <Route
                path={getRoutePath(EBookingManageRoutes.HotelDocuments)}
                component={BookingManagerDownloadsConnected}
              />
              <Route
                path={getRoutePath(EBookingManageRoutes.Uploads)}
                render={routeProps => {
                  return <BookingManagerUploads {...routeProps} bookingUuid={booking.bookingUuid} />;
                }}
              />
              {/* vouchers */}
              {isInternalUser() && (
                <Route exact path={getRoutePath(EBookingManageRoutes.Vouchers)} component={BookingManagerVouchersV2} />
              )}
              {!hideFinance && (
                <Route
                  exact
                  path={getRoutePath(EBookingManageRoutes.Finance)}
                  component={BookingManagerFinanceConnected}
                />
              )}
              {canSeePurchaseOrders() && (
                <Route
                  exact
                  path={getRoutePath(EBookingManageRoutes.PurchaseOrders)}
                  render={routeProps => {
                    return <BookingManagerPurchaseOrders {...routeProps} bookingUuid={booking.bookingUuid} />;
                  }}
                />
              )}
              {isInternalUser() && (
                <Route exact path={getRoutePath(EBookingManageRoutes.Settings)} component={BookingManagerSettings} />
              )}
              <Route
                exact
                path={`${props.match.path}`}
                render={() => <Redirect to={getRoutePath(EBookingManageRoutes.Overview)} />}
              />
            </Switch>
          </section>
        </div>
      </NavigationLayoutConnected>
    </BookingManagerStyles>
  );
};
export default BookingManagerContainer;
